import { useState, useEffect, useMemo, useCallback } from "react";
import api from "../../api/api";
import { toast } from "react-toastify";
import {
  Plus,
  Minus,
  ShoppingCart,
  Search,
  Utensils,
  Users,
  DollarSign,
  Trash2,
  Loader2,
  Phone,
} from "lucide-react";
import * as S from "./styles";

const NewOrder = () => {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Todos");
  const [loading, setLoading] = useState(true);

  const [customer, setCustomer] = useState({
    name: "",
    whatsapp: "",
    orderType: "Local",
    cep: "",
    address_street: "",
    address_number: "",
    address_complement: "",
    address_neighborhood: "",
    paymentMethod: "",
    notes: "",
    paidValue: "",
  });

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await api.get("/products");
        // O "Monte seu Lanche" é montado no site; lanches sem ingrediente em estoque não dá para fazer.
        setProducts(res.data.filter((p) => p.available && !p.is_builder && p.in_stock !== false));
      } catch (err) {
        toast.error("Erro ao carregar cardápio.");
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const handleCEPBlur = async () => {
    const cep = customer.cep.replace(/\D/g, "");
    if (cep.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data = await response.json();
        if (data.erro) return toast.error("CEP não encontrado.");

        setCustomer((prev) => ({
          ...prev,
          address_street: data.logradouro,
          address_neighborhood: data.bairro,
        }));
        toast.info("Endereço localizado!");
      } catch (err) {
        toast.error("Erro ao buscar CEP.");
      }
    }
  };

  const calculateTotal = useCallback(() => {
    return cart.reduce((acc, item) => acc + item.quantity * item.unit_price, 0);
  }, [cart]);

  const changeAmount = useMemo(() => {
    const total = calculateTotal();
    const paid = parseFloat(customer.paidValue) || 0;
    return paid > total ? paid - total : 0;
  }, [customer.paidValue, calculateTotal]);

  const submitOrder = useCallback(async () => {
    if (!customer.name || cart.length === 0 || !customer.paymentMethod) {
      return toast.warn("Preencha Nome, Itens e Pagamento.");
    }

    try {
      const mappedType =
        customer.orderType === "Entrega" ? "delivery" : "balcao";

      let autoNote = "";
      if (customer.orderType === "Local") autoNote = "[COMER NO LOCAL] ";
      if (customer.orderType === "Viagem") autoNote = "[PARA LEVAR] ";

      const finalNotes =
        `${autoNote}${customer.notes || ""}`.trim() || "Sem observações";

      const payload = {
        customer_name: customer.name,
        customer_whatsapp: customer.whatsapp || "N/A",
        total_price: calculateTotal(),
        payment_method: customer.paymentMethod,
        notes: finalNotes,
        type: mappedType,
        address_street:
          customer.orderType === "Entrega"
            ? customer.address_street
            : "Retirada Local",
        address_number: customer.address_number || "N/A",
        address_neighborhood: customer.address_neighborhood || "",
        items: cart,
      };

      await api.post("/orders", payload);
      toast.success("✅ Pedido Ninja enviado!");

      setCart([]);
      setCustomer({
        name: "",
        whatsapp: "",
        orderType: "Local",
        cep: "",
        address_street: "",
        address_number: "",
        address_complement: "",
        address_neighborhood: "",
        paymentMethod: "",
        notes: "",
        paidValue: "",
      });
    } catch (err) {
      toast.error("Erro ao salvar pedido.");
    }
  }, [cart, customer, calculateTotal]);

  // Atalhos de Teclado
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "F2") {
        e.preventDefault();
        submitOrder();
      }
      if (e.key === "Escape") setSearchTerm("");
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [submitOrder]);

  const addToCart = (product) => {
    const exists = cart.find((item) => item.product_id === product.id);
    if (exists) {
      updateQuantity(product.id, 1);
    } else {
      setCart([
        ...cart,
        {
          product_id: product.id,
          name: product.name,
          quantity: 1,
          unit_price: Number(product.price),
        },
      ]);
    }
  };

  const updateQuantity = (id, delta) => {
    setCart((prev) =>
      prev
        .map((item) =>
          item.product_id === id
            ? { ...item, quantity: Math.max(0, item.quantity + delta) }
            : item,
        )
        .filter((i) => i.quantity > 0),
    );
  };

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      const matchesSearch = p.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesCategory =
        selectedCategory === "Todos" || p.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [products, searchTerm, selectedCategory]);

  const categories = ["Todos", ...new Set(products.map((p) => p.category))];

  return (
    <S.Container>
      <S.MainContent>
        <header>
          <div className="header-top">
            <h2>
              <Utensils /> Ninja Burger PDV
            </h2>
            <div className="order-type-toggle">
              {["Local", "Viagem", "Entrega"].map((type) => (
                <button
                  key={type}
                  className={customer.orderType === type ? "active" : ""}
                  onClick={() => setCustomer({ ...customer, orderType: type })}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          <div className="customer-info-grid">
            <S.InputGroup>
              <Users size={18} />
              <input
                placeholder="Cliente"
                value={customer.name}
                onChange={(e) =>
                  setCustomer({ ...customer, name: e.target.value })
                }
              />
            </S.InputGroup>
            <S.InputGroup>
              <Phone size={18} />
              <input
                placeholder="WhatsApp"
                value={customer.whatsapp}
                onChange={(e) =>
                  setCustomer({ ...customer, whatsapp: e.target.value })
                }
              />
            </S.InputGroup>
            <S.InputGroup>
              <DollarSign size={18} />
              <select
                value={customer.paymentMethod}
                onChange={(e) =>
                  setCustomer({ ...customer, paymentMethod: e.target.value })
                }
              >
                <option value="">Pagamento</option>
                <option value="PIX">PIX</option>
                <option value="Cartão">Cartão</option>
                <option value="Dinheiro">Dinheiro</option>
              </select>
            </S.InputGroup>
          </div>

          {customer.orderType === "Entrega" && (
            <S.AddressSection>
              <div className="address-row">
                <input
                  placeholder="CEP"
                  className="cep-input"
                  value={customer.cep}
                  onBlur={handleCEPBlur}
                  onChange={(e) =>
                    setCustomer({ ...customer, cep: e.target.value })
                  }
                />
                <input
                  placeholder="Rua"
                  className="street-input"
                  value={customer.address_street}
                  onChange={(e) =>
                    setCustomer({ ...customer, address_street: e.target.value })
                  }
                />
                <input
                  placeholder="Nº"
                  className="number-input"
                  value={customer.address_number}
                  onChange={(e) =>
                    setCustomer({ ...customer, address_number: e.target.value })
                  }
                />
              </div>
              <div className="address-row">
                <input
                  placeholder="Bairro"
                  value={customer.address_neighborhood}
                  onChange={(e) =>
                    setCustomer({
                      ...customer,
                      address_neighborhood: e.target.value,
                    })
                  }
                />
                <input
                  placeholder="Complemento"
                  value={customer.address_complement}
                  onChange={(e) =>
                    setCustomer({
                      ...customer,
                      address_complement: e.target.value,
                    })
                  }
                />
              </div>
            </S.AddressSection>
          )}

          {customer.paymentMethod === "Dinheiro" && (
            <S.CashSection>
              <label>Valor Recebido:</label>
              <input
                type="number"
                value={customer.paidValue}
                onChange={(e) =>
                  setCustomer({ ...customer, paidValue: e.target.value })
                }
              />
              <div className="change">
                Troco: <strong>R$ {changeAmount.toFixed(2)}</strong>
              </div>
            </S.CashSection>
          )}

          <S.InputGroup style={{ marginTop: "10px", width: "100%" }}>
            <input
              placeholder="Observações do Pedido..."
              value={customer.notes}
              onChange={(e) =>
                setCustomer({ ...customer, notes: e.target.value })
              }
            />
          </S.InputGroup>
        </header>

        <S.FilterBar>
          <div className="search-box">
            <Search size={20} />
            <input
              placeholder="Buscar no cardápio..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="categories">
            {categories.map((cat) => (
              <button
                key={cat}
                className={selectedCategory === cat ? "active" : ""}
                onClick={() => setSelectedCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </S.FilterBar>

        {loading ? (
          <S.LoadingWrapper>
            <Loader2 className="spinner" />
          </S.LoadingWrapper>
        ) : (
          <S.ProductGrid>
            {filteredProducts.map((p) => (
              <S.ProductCard key={p.id} onClick={() => addToCart(p)}>
                <h4>{p.name}</h4>
                <p>{p.description}</p>
                <div className="price-tag">{Number(p.price).toFixed(2)}</div>
                <div className="add">
                  <Plus size={16} />
                </div>
              </S.ProductCard>
            ))}
          </S.ProductGrid>
        )}
      </S.MainContent>

      <S.CartSidebar>
        <S.CartHeader >
          <h3>
            <ShoppingCart /> Carrinho ({cart.length})
          </h3>
          <S.ClearCartButton
            className="clear"
            onClick={() => setCart([])}
            title="Limpar Carrinho"
            disabled={cart.length === 0} 
          >
            <Trash2 size={18} />
          
          </S.ClearCartButton>
        </S.CartHeader>
        <S.CartList>
          {cart.map((item) => (
            <S.CartItem key={item.product_id}>
              <div className="info">
                <strong>{item.name}</strong>
                <span>R$ {(item.unit_price * item.quantity).toFixed(2)}</span>
              </div>
              <div className="controls">
                <button onClick={() => updateQuantity(item.product_id, -1)}>
                  <Minus size={14} />
                </button>
                <span>{item.quantity}</span>
                <button onClick={() => updateQuantity(item.product_id, 1)}>
                  <Plus size={14} />
                </button>
              </div>
            </S.CartItem>
          ))}
        </S.CartList>
        <S.CartFooter>
          <div className="total">
            Total: <strong>R$ {calculateTotal().toFixed(2)}</strong>
          </div>
          <button
            className="confirm"
            onClick={submitOrder}
            disabled={cart.length === 0}
          >
            CONFIRMAR
          </button>
        </S.CartFooter>
      </S.CartSidebar>
    </S.Container>
  );
};

export default NewOrder;
